import React, { useCallback, useMemo, useState } from "react";
import { Alert, Box, Button, IconButton, Tooltip } from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import AllInclusiveRoundedIcon from "@mui/icons-material/AllInclusiveRounded";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNew";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import ReplyRoundedIcon from "@mui/icons-material/ReplyRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
// `expires_at` is a real instant, not a calendar date — formatDateTime, per the
// dates rule in CLAUDE.md.
import formatDateTime from "../../../_src_shared/utils/functions/formatDateTime";
import colors, { status } from "../../../theme/colors";
import { PREFILL_FIELD_LABELS } from "../sendToBorrowerConfig";

/**
 * Conservative ceiling for a whole `mailto:` URL. Outlook has historically cut
 * off around 2048 characters and other clients vary, so stay well under the
 * lowest common limit rather than discover it at the borrower's end.
 */
const MAILTO_SAFE_LENGTH = 1800;

/**
 * Builds the `mailto:` handed to the OS mail client.
 *
 * NOTE: a mailto body is text/plain by definition (RFC 6068) — there is no way
 * to embed a real HTML anchor. Each URL goes on its own line, which is what mail
 * clients need in order to auto-linkify it; wrapping one in punctuation or
 * inlining it mid-sentence is what breaks that detection.
 *
 * Encoded by hand rather than with URLSearchParams: that encodes a space as
 * `+`, which mail clients render literally instead of as a space.
 */
const buildMailtoHref = ({ to, name, subject, lines }) => {
  const greeting = name ? `Hi ${name.split(" ")[0]},` : "Hi,";
  const body = [greeting, "", ...lines, "", "Thank you."].join("\r\n");
  const query = [
    `subject=${encodeURIComponent(subject)}`,
    `body=${encodeURIComponent(body)}`,
  ].join("&");
  return `mailto:${to ? encodeURIComponent(to) : ""}?${query}`;
};

const linkLines = (result) =>
  [result?.config?.emailIntro, "", result?.link].filter(
    (line) => line !== undefined && line !== null,
  );

/**
 * What the card says the borrower will see when they open it.
 *
 * A count rather than a generic blurb: the whole point of the form above was
 * prefilling, and "10 of 11 details prefilled" is the only place the broker gets
 * told it actually took. The counts are computed per KIND upstream, so a credit
 * authorization never claims the loan fields its form does not ask.
 */
const prefillLine = (result) => {
  const { prefilledCount, prefillTotal, config } = result;
  // A dedicated link's own one-liner. "Opens blank" is technically true of it
  // as well, but it reads as a prefill that failed rather than as the defining
  // property of a reusable link.
  if (result.subtitle) return result.subtitle;
  if (!Number.isFinite(prefilledCount) || !prefillTotal) {
    return config?.resultSubtitle || "";
  }
  if (prefilledCount === 0) {
    return "Opens blank — the borrower fills in everything";
  }
  return `Opens with ${prefilledCount} of ${prefillTotal} details prefilled`;
};

const ICON_BTN_SX = {
  width: 34,
  height: 34,
  border: `1px solid ${colors.slate200}`,
  borderRadius: "8px",
  color: colors.slate500,
  "&:hover": { borderColor: colors.slate300, color: colors.slate900 },
};

/** One minted link: identity, actions, and the URL itself. */
const LinkCard = ({ result, index, total, showMailto }) => {
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const { config } = result;

  const mailto = useMemo(
    () =>
      buildMailtoHref({
        to: result.recipient?.email,
        name: result.recipient?.name,
        subject: config.emailSubject,
        lines: linkLines(result),
      }),
    [result, config],
  );
  const mailtoTooLong = mailto.length > MAILTO_SAFE_LENGTH;

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(result.link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (e) {
      // Clipboard is blocked outside a secure context; the URL below is
      // selectable, so a manual copy still works.
      console.error("Clipboard write failed", e);
    }
  }, [result.link]);

  const Icon = config.icon;
  const tint = status[config.tone] || status.info;

  return (
    <Box
      sx={{
        border: `1px solid ${colors.slate200}`,
        borderRadius: "12px",
        backgroundColor: "#fff",
      }}
    >
      <Flx centerY wrap gap={1.5} sx={{ p: 1.75, pb: 1.25 }}>
        <Flx
          centerX
          centerY
          sx={{
            flex: "none",
            width: 34,
            height: 34,
            borderRadius: "9px",
            backgroundColor: tint.bg,
          }}
        >
          {Icon && (
            <Icon
              sx={{
                fontSize: "18px !important",
                color: `${tint.fg} !important`,
              }}
            />
          )}
        </Flx>

        <Flx column gap={0.25} sx={{ minWidth: 0, flex: 1 }}>
          <Flx centerY gap={1} sx={{ minWidth: 0 }}>
            <Txt
              component="div"
              sx={{ fontSize: 14.5, fontWeight: 700, color: colors.slate900 }}
            >
              {config.successNoun || config.shortName}
            </Txt>
            {/* Only when there are two: a lone "1 of 1" is noise, but on a
                "Both" send the order is meaningful — the application is the one
                the borrower should open first. */}
            {total > 1 && (
              <Txt
                component="div"
                sx={{
                  flex: "none",
                  px: 0.75,
                  py: "1px",
                  borderRadius: "999px",
                  fontSize: 11,
                  fontWeight: 700,
                  color: colors.slate500,
                  backgroundColor: colors.slate100,
                }}
              >
                {`${index + 1} of ${total}`}
              </Txt>
            )}
          </Flx>
          <Txt component="div" sx={{ fontSize: 12.5, color: colors.slate500 }}>
            {prefillLine(result)}
          </Txt>
        </Flx>

        <Flx centerY gap={1} sx={{ ml: { sm: "auto" }, flex: "none" }}>
          <Button
            variant="contained"
            size="small"
            onClick={copy}
            startIcon={
              copied ? (
                <CheckRoundedIcon sx={{ fontSize: 16 }} />
              ) : (
                <ContentCopyRoundedIcon sx={{ fontSize: 16 }} />
              )
            }
            sx={{
              minWidth: 112,
              ...(copied && {
                backgroundColor: colors.success,
                "&:hover": { backgroundColor: colors.success },
              }),
            }}
          >
            {copied ? "Copied!" : "Copy link"}
          </Button>

          {/* Only when the server did NOT email it. When it did, the borrower
              already has the link and a second draft in the broker's own client
              is a duplicate they have to decide what to do with. */}
          {showMailto && (
            <Tooltip
              title={
                mailtoTooLong
                  ? "This link is too long to hand to an email client reliably — copy it instead"
                  : "Draft this in your email client"
              }
            >
              {/* span so the Tooltip still fires while the button is disabled */}
              <span>
                <IconButton
                  component="a"
                  href={mailto}
                  disabled={mailtoTooLong}
                  aria-label="Draft in email client"
                  sx={ICON_BTN_SX}
                >
                  <MailOutlineRoundedIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </span>
            </Tooltip>
          )}

          <Tooltip title="Open in new tab">
            <IconButton
              component="a"
              href={result.link}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open in new tab"
              sx={ICON_BTN_SX}
            >
              <OpenInNewRoundedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Flx>
      </Flx>

      {/* The URL sits in its own tinted strip rather than as another line of
          text: it is the thing being handed over, and it is the one piece of
          this card that cannot be recovered once the screen is left. */}
      <Box sx={{ px: 1.75, pb: 1.75 }}>
        <Flx
          centerY
          gap={1}
          sx={{
            backgroundColor: colors.slate100,
            borderRadius: "8px",
            px: 1.25,
            py: 0.875,
          }}
        >
          <Box
            component="code"
            sx={{
              flex: 1,
              minWidth: 0,
              fontSize: 12,
              color: colors.slate600,
              ...(expanded
                ? { wordBreak: "break-all", maxHeight: 140, overflow: "auto" }
                : {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }),
            }}
          >
            {result.link}
          </Box>
          <Button
            variant="text"
            size="small"
            onClick={() => setExpanded((prev) => !prev)}
            sx={{ flex: "none", fontSize: 12, minWidth: 0, px: 0.5 }}
          >
            {expanded ? "Collapse" : "Show full"}
          </Button>
        </Flx>
      </Box>

      {(result.notPrefilled?.length > 0 ||
        result.droppedFields?.length > 0) && (
        <Flx column gap={1} sx={{ px: 1.75, pb: 1.75 }}>
          {/* Not a warning: these were withheld on purpose, and the borrower is
              simply asked for them. Naming the form makes it obvious why the
              same value travelled on the other link. */}
          {result.notPrefilled?.length > 0 && (
            <Alert severity="info" sx={{ py: 0.25 }}>
              {`${result.notPrefilled
                .map((name) => PREFILL_FIELD_LABELS[name] || name)
                .join(", ")} ${
                result.notPrefilled.length === 1 ? "is" : "are"
              } not carried on a ${config.shortName} — the borrower fills ${
                result.notPrefilled.length === 1 ? "it" : "them"
              } in.`}
            </Alert>
          )}
          {result.droppedFields?.length > 0 && (
            <Alert severity="warning" sx={{ py: 0.25 }}>
              {`These prefill values were not saved to the link: ${result.droppedFields.join(", ")}. The borrower will be asked for them.`}
            </Alert>
          )}
        </Flx>
      )}
    </Box>
  );
};

/** One item of the quiet meta row under the callout. */
const MetaItem = ({ icon: Icon, children }) => (
  <Flx centerY gap={0.625} sx={{ flex: "none" }}>
    <Icon sx={{ fontSize: "14px !important", color: colors.slate400 }} />
    <Txt component="div" sx={{ fontSize: 12, color: colors.slate500 }}>
      {children}
    </Txt>
  </Flx>
);

/**
 * The whole result of a generate: one card holding every minted link.
 *
 * Deliberately one panel rather than one per link. Who it went to, the
 * copy-it-now caveat, and the way out of the flow are properties of the handoff,
 * not of each URL — repeating them per link made two links read as two separate
 * jobs when they are one.
 *
 * Structure, top to bottom: what happened → the link(s) → the one thing the
 * broker has to act on before leaving → quiet metadata → exits.
 */
const GeneratedLinksPanel = ({
  results = [],
  summary,
  onRegenerate,
  onClose,
  // Footer actions, shown only when handed a callback. `onSendAnother` clears
  // the result and blanks the form for a different borrower; `onDone` leaves
  // the flow (closes the host dialog, or navigates back).
  onSendAnother,
  onDone,
  // Drops the panel's own card frame, for a host that already draws one (the
  // Shareable links modal) — otherwise the result renders as a card in a card.
  frameless = false,
}) => {
  const links = results.filter((result) => result.link);
  const emailedTo = links.find((result) => result.emailedTo)?.emailedTo;
  const emailError = links.find((result) => result.emailError)?.emailError;
  const expiresAt = links[0]?.expiresAt || null;
  const multiple = links.length > 1;
  /**
   * A dedicated (evergreen) result. Nearly every piece of copy below is wrong
   * for one: it is permanent rather than one-time, re-readable rather than
   * unrecoverable, and reusable rather than addressed to a borrower. Derived
   * from the results rather than passed in, so a caller cannot render the
   * one-time warning over a link that does not have that property.
   */
  const evergreen =
    links.length > 0 && links.every((result) => result.linkMode === "evergreen");

  if (links.length === 0) return null;

  const them = multiple ? "them" : "it";
  const theseLinks = multiple ? "these links" : "this link";

  return (
    <Box
      sx={
        frameless
          ? {}
          : {
              backgroundColor: "#fff",
              border: `1px solid ${colors.slate200}`,
              borderRadius: "16px",
              boxShadow: "0 1px 2px rgba(15,23,42,.04)",
              p: { xs: 2.25, sm: 3 },
            }
      }
    >
      <Flx column gap={2.5}>
        {/* What happened. Conditional on `email_sent`, so a failed send is never
            reported as a delivery. */}
        <Flx gap={1.5} sx={{ alignItems: "flex-start" }}>
          <Flx
            centerX
            centerY
            sx={{
              flex: "none",
              width: 30,
              height: 30,
              borderRadius: "50%",
              backgroundColor: status.success.bg,
            }}
          >
            <CheckRoundedIcon
              sx={{
                fontSize: "18px !important",
                color: `${status.success.fg} !important`,
              }}
            />
          </Flx>

          <Flx column gap={0.375} sx={{ minWidth: 0, flex: 1 }}>
            <Txt
              component="div"
              sx={{ fontSize: 17, fontWeight: 800, letterSpacing: "-0.01em" }}
            >
              {evergreen
                ? multiple
                  ? "Your shareable links"
                  : "Your shareable link"
                : emailedTo
                  ? multiple
                    ? "Links emailed"
                    : "Link emailed"
                  : multiple
                    ? "Links generated"
                    : "Link generated"}
            </Txt>
            <Txt
              component="div"
              sx={{ fontSize: 13, color: colors.slate500, lineHeight: 1.5 }}
            >
              {evergreen ? (
                `Nothing was emailed. Copy ${them} and paste ${them} wherever you like — every application that comes back is attributed to you.`
              ) : emailedTo ? (
                <>
                  {"Sent to "}
                  <Box
                    component="strong"
                    sx={{ fontWeight: 700, color: colors.slate900 }}
                  >
                    {emailedTo}
                  </Box>
                  {` — the borrower can open ${multiple ? "them" : "it"} right away.`}
                </>
              ) : (
                `Not emailed — copy ${them} and send ${them} yourself.`
              )}
            </Txt>
            {/* Reads back who the link is actually for, so one generated for the
                wrong borrower is obvious before it is handed over. */}
            {summary && (
              <Txt
                component="div"
                sx={{ fontSize: 12.5, color: colors.slate400 }}
              >
                {summary}
              </Txt>
            )}
          </Flx>

          {onClose && (
            <Tooltip title="Dismiss">
              <IconButton
                size="small"
                onClick={onClose}
                aria-label="Dismiss links panel"
                sx={{ flex: "none", mt: -0.5, mr: -0.5 }}
              >
                <CloseRoundedIcon sx={{ fontSize: 19 }} />
              </IconButton>
            </Tooltip>
          )}
        </Flx>

        {emailError && (
          <Alert severity="warning">
            {`The ${multiple ? "links were" : "link was"} created, but the email did not send: ${emailError}. Copy and send ${them} yourself.`}
          </Alert>
        )}

        <Flx column gap={1.5}>
          {links.map((result, index) => (
            <LinkCard
              key={result.config.linkKind}
              result={result}
              index={index}
              total={links.length}
              showMailto={!emailedTo}
            />
          ))}
        </Flx>

        {/* Amber, because on a TARGETED link this is an instruction with a
            deadline: the mint returns the raw URL exactly once — the server
            keeps only a sha256, and its stored `link_url` is a display-safe
            prefix — so leaving without copying means re-minting, not
            re-fetching.

            None of that is true of a dedicated link, whose whole purpose is to
            be permanent, so it gets the opposite note in a neutral tone.
            Showing the amber one there would train LOs to ignore it on the
            links where it actually matters. */}
        {evergreen ? (
          <Flx
            gap={1.25}
            sx={{
              alignItems: "flex-start",
              backgroundColor: colors.slate50,
              border: `1px solid ${colors.slate200}`,
              borderRadius: "10px",
              px: 1.5,
              py: 1.25,
            }}
          >
            <AllInclusiveRoundedIcon
              sx={{
                flex: "none",
                mt: "1px",
                fontSize: "15px !important",
                color: `${colors.slate400} !important`,
              }}
            />
            <Txt
              component="div"
              sx={{ fontSize: 12.5, color: colors.slate600, lineHeight: 1.55 }}
            >
              <Box
                component="strong"
                sx={{ fontWeight: 700, color: colors.slate900 }}
              >
                Safe to reuse and safe to lose.
              </Box>{" "}
              {`${
                multiple ? "These are your links" : "This is your link"
              } — ${them} never ${
                multiple ? "expire" : "expires"
              }, and coming back to this screen shows you the same ${
                multiple ? "URLs" : "URL"
              } rather than minting ${multiple ? "new ones" : "a new one"}.`}
            </Txt>
          </Flx>
        ) : (
        <Flx
          gap={1.25}
          sx={{
            alignItems: "flex-start",
            backgroundColor: status.warning.bg,
            border: "1px solid #FDE68A",
            borderRadius: "10px",
            px: 1.5,
            py: 1.25,
          }}
        >
          <LockOutlinedIcon
            sx={{
              flex: "none",
              mt: "1px",
              fontSize: "15px !important",
              color: `${status.warning.fg} !important`,
            }}
          />
          <Txt
            component="div"
            sx={{ fontSize: 12.5, color: "#92400E", lineHeight: 1.55 }}
          >
            <Box component="strong" sx={{ fontWeight: 700 }}>
              Need your own copy? Take it now.
            </Box>{" "}
            {`For security, the full URL can't be retrieved after you leave this screen.${
              emailedTo
                ? ` The borrower already has ${them} by email — sending again mints a fresh link rather than showing ${theseLinks} a second time.`
                : ` Copy ${them} before you go — sending again mints a fresh link rather than showing ${theseLinks} a second time.`
            }`}
          </Txt>
        </Flx>
        )}

        {/* Everything that is true about the link but that nobody has to act
            on, in one line instead of scattered through the card. */}
        <Flx wrap gap={2.5} centerY>
          <MetaItem icon={ScheduleRoundedIcon}>
            {evergreen
              ? "Never expires"
              : emailedTo
                ? "Emailed just now"
                : "Generated just now"}
          </MetaItem>
          {expiresAt && (
            <MetaItem icon={EventOutlinedIcon}>
              {/* The "date" pattern is already "MMM D, YYYY" — spelling it out
                  here as a custom pattern would be date-fns syntax against a
                  dayjs formatter and render the literal "yyyy". */}
              {`Expires ${formatDateTime(expiresAt, "date")}`}
            </MetaItem>
          )}
          {/* Attribution is server-side: the mint stamps the caller's user id on
              the link, so whatever the borrower submits comes back to them. */}
          <MetaItem icon={ReplyRoundedIcon}>
            Submission routes back to you
          </MetaItem>
        </Flx>

        <Flx centerY wrap gap={1.25}>
          {onRegenerate && (
            <Tooltip
              title={
                evergreen
                  ? "Back to the options — your link stays exactly as it is."
                  : "Back to the form with your details — the link above stays valid until it expires."
              }
            >
              <Button
                variant="outlined"
                size="small"
                onClick={onRegenerate}
                startIcon={<RefreshRoundedIcon sx={{ fontSize: 16 }} />}
              >
                {evergreen ? "Back to options" : "Regenerate"}
              </Button>
            </Tooltip>
          )}
          <Box sx={{ flex: 1 }} />
          {onSendAnother && (
            <Button variant="outlined" size="small" onClick={onSendAnother}>
              Send another
            </Button>
          )}
          {onDone && (
            <Button variant="contained" size="small" onClick={onDone}>
              Done
            </Button>
          )}
        </Flx>
      </Flx>
    </Box>
  );
};

export default GeneratedLinksPanel;
